import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import {
  BUILTIN_CTA_HREFS,
  KILL_SWITCH_MAX_PROPAGATION_SECONDS,
  canPerform,
  isHttpsUrl,
} from "@vital/shared";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/Card";
import { InspectPanel } from "../components/InspectPanel";
import { LtrIsolate } from "../components/LtrIsolate";
import { PasswordConfirmModal } from "../components/PasswordConfirmModal";
import { StickyFormActions } from "../components/StickyFormActions";
import { OpsEmptyState, SectionHeader, InventorySkeleton } from "../components/ops-display";
import { cmsStrings } from "../terminology";
import {
  CtaAddForm,
  CtaFilterBar,
  CtaInspectSummary,
  CtaInventoryList,
  KillSwitchInspectPanel,
} from "../components/settings-inventory";
import { useAuth } from "../context/AuthContext";
import { api, ApiError, type KillSwitchAdminState } from "../lib/api";
import {
  CTA_ALLOWLIST_NOTE,
  KILL_SWITCH_LABEL,
  KILL_SWITCH_SCOPE_NOTE,
  SETTINGS_PAGE_NOTE,
  buildCtaInventory,
  ctaInventoryCounts,
  filterAndSortCta,
  type CtaDestinationRow,
  type CtaSortKey,
} from "../lib/settings-ops";

function urlsEqual(a: string[], b: string[]) {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((url, i) => url === sortedB[i]);
}

export function SettingsPage() {
  const { user } = useAuth();
  const isPlatformOwner = user?.platformRole === "owner";
  const canManageSettings = canPerform({
    platformRole: user?.platformRole ?? null,
    membershipRole: user?.membershipRole ?? null,
    legacyRole: user?.role ?? null,
    permission: "settings.manage",
  });

  const [urls, setUrls] = useState<string[]>([]);
  const [savedUrls, setSavedUrls] = useState<string[]>([]);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [newUrl, setNewUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [query, setQuery] = useState("");
  const [listFilter, setListFilter] = useState("all");
  const [sort, setSort] = useState<CtaSortKey>("url");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [kill, setKill] = useState<KillSwitchAdminState | null>(null);
  const [killReason, setKillReason] = useState("");
  const [killModal, setKillModal] = useState<"enable" | "disable" | null>(null);
  const [killPassword, setKillPassword] = useState("");
  const [killBusy, setKillBusy] = useState(false);

  const dirty = useMemo(() => !urlsEqual(urls, savedUrls), [urls, savedUrls]);

  const inventory = useMemo(() => buildCtaInventory(urls, savedUrls), [urls, savedUrls]);
  const counts = useMemo(() => ctaInventoryCounts(inventory), [inventory]);
  const filtered = useMemo(
    () => filterAndSortCta(inventory, { query, filter: listFilter, sort }),
    [inventory, query, listFilter, sort],
  );
  const selected = useMemo(
    () => inventory.find((r) => r.id === selectedId) ?? null,
    [inventory, selectedId],
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cta, killState] = await Promise.all([
        api.getApprovedCtaSettings(),
        canManageSettings ? api.getKillSwitchState() : Promise.resolve(null),
      ]);
      setUrls(cta.urls);
      setSavedUrls(cta.urls);
      setUpdatedAt(cta.updatedAt);
      setKill(killState);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "بارگذاری ناموفق");
    } finally {
      setLoading(false);
    }
  }, [canManageSettings]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    if (selectedId && !inventory.some((r) => r.id === selectedId)) {
      setSelectedId(null);
    }
  }, [inventory, selectedId]);

  function handleAdd(e: FormEvent) {
    e.preventDefault();
    const trimmed = newUrl.trim();
    if (!trimmed) return;
    if (!isHttpsUrl(trimmed)) {
      setError(cmsStrings.settingsComposer.httpsOnlyError);
      return;
    }
    if ((BUILTIN_CTA_HREFS as readonly string[]).includes(trimmed)) {
      setError(cmsStrings.settingsComposer.builtinDuplicateError);
      return;
    }
    if (urls.includes(trimmed)) {
      setError(cmsStrings.settingsComposer.alreadyAddedError);
      return;
    }
    setUrls([...urls, trimmed]);
    setNewUrl("");
    setError(null);
    setSuccess(null);
  }

  function handleRemove(url: string) {
    setUrls(urls.filter((u) => u !== url));
    setSuccess(null);
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await api.setApprovedCtaUrls(urls);
      setUrls(result.urls);
      setSavedUrls(result.urls);
      setUpdatedAt(result.updatedAt);
      setSuccess(cmsStrings.settingsComposer.savedSuccess);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "ذخیره ناموفق");
    } finally {
      setSaving(false);
    }
  }

  async function handleKillConfirm(e: FormEvent) {
    e.preventDefault();
    if (!killModal) return;
    setKillBusy(true);
    setError(null);
    try {
      const next =
        killModal === "enable"
          ? await api.enableKillSwitch({
              password: killPassword,
              reason: killReason.trim() || undefined,
            })
          : await api.disableKillSwitch(killPassword);
      setKill(next);
      setKillModal(null);
      setKillPassword("");
      setKillReason("");
      setSuccess(
        killModal === "enable"
          ? cmsStrings.settingsComposer.killEnabledSuccess
          : cmsStrings.settingsComposer.killDisabledSuccess,
      );
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : cmsStrings.settingsComposer.killFailedFallback,
      );
    } finally {
      setKillBusy(false);
    }
  }

  function renderCtaActions(row: CtaDestinationRow): ReactNode {
    if (row.kind !== "approved") return null;
    return (
      <button
        type="button"
        className="btn btn--ghost btn--sm"
        onClick={() => handleRemove(row.url)}
      >
        {cmsStrings.settingsComposer.remove}
      </button>
    );
  }

  const sc = cmsStrings.settingsComposer;

  return (
    <>
      <PageHeader title={cmsStrings.nav.settings} />

      <aside
        className="ops-safety-summary ops-safety-summary--privacy"
        role="note"
        data-vt-settings-safety
      >
        <p className="ops-safety-summary__eyebrow">حریم تنظیمات</p>
        <ul className="ops-safety-summary__points">
          <li>فقط HTTPS برای CTA؛ پیش‌فرض‌ها فقط‌خواندنی؛ ذخیره نهایی روی سرور</li>
          <li>
            {KILL_SWITCH_LABEL}: مخفی‌سازی اطلاعیه‌ها، پیشنهادها، و تبلیغات ویژهٔ راه دور
          </li>
          <li>ورود/CHAP، پورتال محلی، و RouterOS دست‌نخورده — بدون وب‌هوک یا راز</li>
        </ul>
        <details className="ops-safety-disclosure">
          <summary>متن کامل حریم تنظیمات و قطع فوری</summary>
          <p>{SETTINGS_PAGE_NOTE}</p>
          <p>{CTA_ALLOWLIST_NOTE}</p>
          <p>{KILL_SWITCH_SCOPE_NOTE}</p>
        </details>
      </aside>

      {error && <p className="banner banner--error">{error}</p>}
      {success && <p className="banner banner--success">{success}</p>}
      {dirty && !loading && (
        <p className="banner banner--warning" role="status">
          {sc.dirtyBanner}
        </p>
      )}
      {kill?.active && (
        <p className="banner banner--warning" role="status">
          {KILL_SWITCH_LABEL} {sc.activeBannerBefore}{" "}
          <LtrIsolate mono>
            {kill.maxPropagationSeconds ?? KILL_SWITCH_MAX_PROPAGATION_SECONDS}s
          </LtrIsolate>
          {sc.activeBannerAfter}
        </p>
      )}

      <div className="page-stack page-stack--inventory">
        {canManageSettings && (
          <Card>
            <SectionHeader
              title={KILL_SWITCH_LABEL}
              description={cmsStrings.killSwitch.platformSectionDescription}
            />
            <KillSwitchInspectPanel
              kill={kill}
              loading={loading}
              isPlatformOwner={isPlatformOwner}
              killReason={killReason}
              onKillReasonChange={setKillReason}
              onEnableClick={() => {
                setKillModal("enable");
                setKillPassword("");
              }}
              onDisableClick={() => {
                setKillModal("disable");
                setKillPassword("");
              }}
            />
          </Card>
        )}

        <div
          className={`content-ops-layout${selected ? " content-ops-layout--inspect-open" : ""}`}
          data-vt-content-ops
          data-vt-inspect-open={selected ? "true" : "false"}
        >
          <Card className="content-ops-layout__list inventory-surface" bodyClassName="card__body--flush">
            <SectionHeader title={cmsStrings.settingsUi.ctaListTitle} />
            <CtaFilterBar
              filter={listFilter}
              onFilterChange={setListFilter}
              query={query}
              onQueryChange={setQuery}
              sort={sort}
              onSortChange={setSort}
              counts={counts}
              updatedAt={updatedAt}
            />
            {loading ? (
              <InventorySkeleton />
            ) : inventory.length === 0 ? (
              <OpsEmptyState title={cmsStrings.settingsUi.emptyCtaTitle} />
            ) : filtered.length === 0 ? (
              <OpsEmptyState title={cmsStrings.inventory.noMatchTitle} />
            ) : (
              <CtaInventoryList
                rows={filtered}
                selectedId={selectedId}
                onSelect={setSelectedId}
                renderActions={renderCtaActions}
              />
            )}
            <CtaAddForm newUrl={newUrl} onNewUrlChange={setNewUrl} onSubmit={handleAdd} />
            <StickyFormActions>
              <button
                type="button"
                className="btn btn--primary btn--sm"
                disabled={saving || loading || !dirty}
                onClick={() => void handleSave()}
              >
                {saving ? sc.saving : sc.saveFinal}
              </button>
            </StickyFormActions>
          </Card>
          {selected && (
            <InspectPanel
              title={cmsStrings.settingsUi.ctaInspectTitle}
              className="content-inspect-panel"
            >
              <CtaInspectSummary
                item={selected}
                counts={counts}
                actions={renderCtaActions(selected)}
              />
            </InspectPanel>
          )}
        </div>
      </div>

      {killModal && (
        <PasswordConfirmModal
          title={killModal === "enable" ? sc.enableConfirmTitle : sc.disableConfirmTitle}
          description={
            killModal === "enable" ? KILL_SWITCH_SCOPE_NOTE : sc.disableImpact
          }
          password={killPassword}
          onPasswordChange={setKillPassword}
          confirming={killBusy}
          confirmLabel={
            killModal === "enable" ? sc.enableConfirmLabel : sc.disableConfirmLabel
          }
          tone={killModal === "enable" ? "critical" : "primary"}
          onCancel={() => setKillModal(null)}
          onConfirm={handleKillConfirm}
        />
      )}
    </>
  );
}
